import dayjs from 'dayjs';
import {
  clone,
  cloneDeep,
  debounce,
  isArray,
  isNil,
  isUndefined,
  orderBy,
  pick,
  flatMap,
  map
} from 'lodash';

import { isEmpty } from '../utils';
import { deepDiffObj, isEqualWithEmpty } from '../utils/deepDiff';
import { transformVO } from '../utils/transformVO';
import { usePromise } from '../utils/usePromise';
import { useRootInstance } from '../utils/useRootInstance';
import { useVuex } from '../utils/useVuex';
import { computed, ref, set, toRaw, watch } from '@vue/composition-api';
import { createEventHook } from '@vueuse/core';
import { validateForm } from '@zk/framework-vue';

import {
  createIssue,
  getMemberList,
  getSelectOptions,
  updateIssue as updateIssueApi
} from '../api/issue';
import { OptionItem } from '../models/field';
import { IssueArea, IssueFieldInfo } from '../models/issue';
import { ActionStore, IssueDrawerActionCreate } from './action';
import { IssueStore } from './issue';
import { IssueTypeStore } from './issueType';
import { LayoutStore } from './layout';
import { ProjectStore } from './project';
import { ShareStore } from './share';

export const useFields = (
  actionStore: ActionStore,
  projectStore: ProjectStore,
  issueTypeStore: IssueTypeStore,
  layoutStore: LayoutStore,
  issueStore: IssueStore,
  shareStore: ShareStore
) => {
  const fieldValues = ref<Record<string, any>>({});

  const vuex = useVuex();
  const instance = useRootInstance();

  const valueChangedEvent = createEventHook<{
    field: IssueFieldInfo;
    value: any;
  }>();

  const valueSavedEvent = createEventHook<Record<string, any>>();

  const formatToNum = (val: string) => (isNaN(Number(val)) ? val : +val);
  const formatToArr = (defaultOptions: string) =>
    (defaultOptions || '').split(',').map(formatToNum);

  const filterOptions = (
    options: Array<{ value: number | string; accountEnabled?: 0 | 1 }>,
    val: number | string | Array<string | number>
  ) => {
    const hasOption = (v: number | string) =>
      options.find((f) => f.value === v && f.accountEnabled);

    if (Array.isArray(val)) {
      return val.filter(hasOption);
    }
    return hasOption(val) ? val : undefined;
  };

  const {
    refresh: refreshOptions,
    result: selectOptions,
    onReceive: onGetSelectOptions,
    isLoading: isLoadingSelectOptions
  } = usePromise(
    (
      param?: { projectId: number; issueTypeId: number } | { issueId: number }
    ) => (param ? getSelectOptions(param) : []),
    [],
    { immediate: false }
  );

  onGetSelectOptions((res) => {
    if (actionStore.action.value?.type === 'Create') {
      const statusOptionsValues = optionsMap.value.status.map((f) => f.value);
      if (
        optionsMap.value.status &&
        (!fieldValues.value.status ||
          !statusOptionsValues
            .includes(fieldValues.value.status))
      ) {
        //处理默认状态不存在options中的情况
        const defaultDataStatusExist = statusOptionsValues.includes(actionStore.action.value?.defaultData?.status);
        const statusValue = defaultDataStatusExist ? actionStore.action.value?.defaultData?.status : optionsMap.value.status[0].value;
        actionStore.action.value.defaultData.status = statusValue;
        fieldValues.value = { ...fieldValues.value, status: statusValue };
      }
    }
  });

  const { refresh: refreshMembers, result: members } = usePromise(
    (projectId?: number) => (projectId ? getMemberList(projectId) : []),
    [],
    { immediate: false }
  );

  const optionsMap = computed(() =>
    selectOptions.value.reduce((map, option) => {
      map[option.fieldCode] = option.optionVOList;
      return map;
    }, {} as Record<string, OptionItem[]>)
  );

  watch(selectOptions, () => {
    if (selectOptions.value && actionStore.action.value?.type === 'Create') {
      const action = actionStore.action.value;
      layoutStore.allFields.value.forEach(
        ({ defaultOptions, fieldCode, controlCode, multipleChoice }) => {
          if (controlCode === 'member' && optionsMap.value[fieldCode]) {
            if (Reflect.has(action.defaultData, fieldCode)) {
              const value = filterOptions(
                optionsMap.value[fieldCode],
                clone(action.defaultData[fieldCode])
              );
              set(fieldValues.value, fieldCode, value);
              action.defaultData[fieldCode] = value;
            } else if (!isNil(defaultOptions)) {
              const value = filterOptions(
                optionsMap.value[fieldCode],
                multipleChoice
                  ? formatToArr(defaultOptions)
                  : formatToNum(defaultOptions)
              );

              set(fieldValues.value, fieldCode, value);
              action.defaultData[fieldCode] = value;
            }
          }
        }
      );
    }
  });

  watch(
    () => [actionStore.action.value, issueStore.issueAssemble.value],
    () => {
      if (issueStore.issueAssemble.value) {
        if (actionStore.action.value?.type === 'Edit') {
          const values = cloneDeep(
            issueStore.issueAssemble.value.issueVO.data || {}
          );

          values.relationship = isEmpty(values.relationship)
            ? issueStore.issueAssemble.value.issueRelationshipVOList?.map(
              (item) => ({
                ...item,
                statusVO: item.status,
                status: item.status.id
              })
            ) || []
            : values.relationship;

          values.attachment =
            issueStore.issueAssemble.value.fileOuterVOList?.map((item) => ({
              id: item.fileKey,
              name: `${item.fileName}${item.fileSuffix}`,
              type: item.fileType,
              url: item.originalUrl || item.fileDownloadPath, // originalUrl
              time: instance.$options.filters.formatDateTime(item.uploadTime),
              size: item.fileSize,
              uploadDate: window.Vue.filter('formatCalendarDateTime')(
                item.uploadTime
              ),
              avatar: item.uploadUserHead,
              userName: item.uploadUserName,
              ...item
            })) || [];

          values.subtask = fields.value.some(
            (field) => field.fieldCode === 'subtask'
          )
            ? orderBy(
              issueStore.issueAssemble.value.issueSubTaskVOList?.map(
                (item) => ({
                  id: item.id,
                  assignee: item.assignee,
                  assigneeVO: item.assigneeVO,
                  status: item.statusId,
                  stateCategory: item.status.stateCategory,
                  summary: item.summary,
                  estimatePoint: item.estimatePoint,
                  dueDate: item.dueDate,
                  startDate: item.startDate,
                  estimateLabour: item.estimateLabour,
                  actualLabour: item.actualLabour,
                  remainingLabour: item.remainingLabour,
                  labourRecord: item.labourRecord,
                  remainingLabourModified: item.remainingLabourModified,
                  createTime: item.createTime,
                  createMode: item.createMode
                })
              ) || [],
              ['stateCategory', 'dueDate', 'createTime'],
              ['asc', 'asc', 'asc']
            )
            : [];
          values.dateRange = [
            values.startDate, values.dueDate
          ];

          values.docs = flatMap(values.docsVO, (list, key) => {
            return map(list, (item) => ({
              ...item,
              createBy: item.docCreateByVO,
              docType: key
            }));
          });

          fieldValues.value = values;
        } else if (actionStore.action.value?.type === 'Create') {
          let values: Record<string, any> = {};
          if (actionStore.action.value.defaultData) {
            values = cloneDeep(actionStore.action.value?.defaultData);
          }

          layoutStore.allFields.value.forEach(
            ({
              defaultOptions,
              defaultValue,
              fieldCode,
              controlCode,
              multipleChoice,
              autoCalculateFlag
            }) => {
              if (
                isNil(values[fieldCode]) &&
                !isNil(defaultOptions) &&
                (!isEmpty(defaultOptions) || defaultOptions === '')
              ) {
                if (controlCode === 'select') {
                  (
                    actionStore.action.value as IssueDrawerActionCreate
                  ).defaultData[fieldCode] = values[fieldCode] =
                    formatToArr(defaultOptions);
                }

                if (['single-select', 'radio'].includes(controlCode)) {
                  const action = actionStore.action
                    .value as IssueDrawerActionCreate;
                  if (fieldCode === 'estimatePoint' && defaultOptions === '') {
                    if (autoCalculateFlag === 0) {
                      action.defaultData[fieldCode] = values[fieldCode] = '';
                      action.defaultData.autoCalculateFlag =
                        values.autoCalculateFlag = 0;
                    } else if (autoCalculateFlag === 1) {
                      action.defaultData[fieldCode] = values[fieldCode] = null;
                      action.defaultData.autoCalculateFlag =
                        values.autoCalculateFlag = 1;
                    }
                  } else {
                    (
                      actionStore.action.value as IssueDrawerActionCreate
                    ).defaultData[fieldCode] = values[fieldCode] =
                      formatToNum(defaultOptions);
                  }
                }

                if (controlCode === 'member' && optionsMap.value[fieldCode]) {
                  values[fieldCode] = filterOptions(
                    optionsMap.value[fieldCode],
                    multipleChoice
                      ? formatToArr(defaultOptions)
                      : formatToNum(defaultOptions)
                  );
                }
              }

              if (controlCode === 'formula' && values[fieldCode]) {
                (
                  actionStore.action.value as IssueDrawerActionCreate
                ).defaultData[fieldCode] = values[fieldCode] =
                  formatToArr(defaultOptions || '');
              }

              if ((controlCode === 'rich-text' || controlCode === 'long-text') && !values[fieldCode]) {
                (
                  actionStore.action.value as IssueDrawerActionCreate
                ).defaultData[fieldCode] = values[fieldCode] = controlCode === 'rich-text' ? defaultValue?.split('\n').map((t:string)=> `<p>${t}</p>`).join('') : defaultValue;
              }
            }
          );

          if (values.subtask) {
            const unFinish =
              issueStore.issueAssemble.value?.stateAssociateVOList?.find(
                (item) => item.stateCategory === 2
              )?.stateId;

            values.subtask = values.subtask.map(
              (task: { summary: string }) => ({
                summary: task.summary,
                status: unFinish,
                stateCategory: 2,
                dueDate: null,
                startDate: null,
                estimatePoint: null,
                assignee: null,
                estimateLabour: null,
                actualLabour: null,
                remainingLabour: null,
                labourRecord: null,
                remainingLabourModified: 0
              })
            );
          }

          if (values.attachment) {
            values.attachment =
              values.attachment.map((item: any) => ({
                id: item.fileKey,
                name: `${item.fileName}${item.fileSuffix}`,
                type: item.fileType,
                url: item.originalUrl || item.fileDownloadPath, // originalUrl
                time: instance.$options.filters.formatDateTime(item.uploadTime),
                size: item.fileSize,
                uploadDate: window.Vue.filter('formatCalendarDateTime')(
                  item.uploadTime
                ),
                avatar: item.uploadUserHead,
                userName: item.uploadUserName,
                ...item
              })) || [];
          }

          if (
            values.autoCalculateFlag === 0 &&
            !Reflect.has(values, 'estimatePoint')
          ) {
            values.estimatePoint = '';
          }

          fieldValues.value = values;
        } else {
          fieldValues.value = {};
        }
      } else {
        fieldValues.value = {};
      }
    }
  );

  watch(actionStore.action, (action) => {
    if (action?.type === 'Edit') {
      refreshOptions({ issueId: action.issueId });
    } else if (action?.type === 'Create') {
      if (
        action.issueTypeId &&
        action.projectId &&
        !isLoadingSelectOptions.value
      ) {
        refreshOptions({
          projectId: action.projectId,
          issueTypeId: action.issueTypeId
        });
      }
    }
  });

  watch(
    [projectStore.currentProjectId, issueTypeStore.currentTypeId],
    ([projectId, typeId]) => {
      !shareStore.isVisitor.value && refreshMembers(projectId);
      if (projectId && typeId) {
        if (
          actionStore.action.value?.type === 'Edit' &&
          (selectOptions.value.length || isLoadingSelectOptions.value) &&
          typeId ===
            (issueStore.issueAssemble.value?.issueVO.data.issueTypeId as number)
        ) {
          return;
        }
        if (!isLoadingSelectOptions.value) {
          refreshOptions({ projectId, issueTypeId: typeId });
        }
      }
    },
    { immediate: true }
  );

  const fields = computed(() => {
    let areaData: IssueArea[] = [];
    if (actionStore.action.value?.type === 'Edit') {
      areaData = issueStore.issueAssemble.value?.issueAreaVOList || [];
    } else if (
      actionStore.action.value?.type === 'Create' ||
      actionStore.action.value?.type === 'PreviewType'
    ) {
      areaData = layoutStore.fieldAreas.value || [];
    }

    return areaData.flatMap((area) =>
      area.groupList.flatMap((group) =>
        group.issueFieldOrdinateVOList.flatMap((o) => o.issueFieldInfoVo)
      )
    );
  });

  const fieldCodeMap = computed(() =>
    fields.value.reduce((map, field) => {
      map[field.fieldCode] = field;
      if (field.childFields) {
        field.childFields.forEach((item) => {
          map[item.fieldCode] = item;
        });
      }
      return map;
    }, {} as Record<string, IssueFieldInfo>)
  );
  const setFieldValue = (fieldCode: string, value: any) => {
    const rawValue = toRaw(value);

    const oldValue = toRaw(fieldValues.value[fieldCode]);
    set(fieldValues.value, fieldCode, rawValue);
    const field = fieldCodeMap.value[fieldCode];
    if (field) {
      valueChangedEvent.trigger({
        field,
        value: rawValue
      });
    }
    // console.log('setFiel dValue', fieldCode, rawValue, fieldValues.value);
  };

  const updateIssueImmediately = (id: number, diff: Record<string, any>) => {
    return updateIssueApi(id, diff);
  };

  const omitNoValidateFields = (diff: Record<string, any>, rootForm: any) => {
    return new Promise<Record<string, any>>((resolve) => {
      const validateKeys: string[] = [];
      const keys = Object.keys(diff);
      let count = keys.length;

      if (count === 0) {
        resolve({});
        return;
      }
      keys.map((key) => {
        const formItem = rootForm.fields.find((f: any) => f.prop === key);
        if (formItem) {
          formItem.validate('', (error?: string) => {
            if (!error) {
              validateKeys.push(key);
            }
            count--;
            if (count === 0) {
              resolve(pick(diff, validateKeys));
            }
          });
        } else {
          validateKeys.push(key);
          count--;
          if (count === 0) {
            resolve(pick(diff, validateKeys));
          }
        }
      });
    });
  };

  const saveChangedImmediately = async () => {
    if (shareStore.isVisitor.value) {
      return;
    }
    const rootForm = (document.getElementById('issue-root-form') as any)
      ?.__vue__;
    if (
      actionStore.action.value?.type === 'Edit' &&
      issueStore.issueAssemble.value &&
      fieldValues.value &&
      rootForm
    ) {
      const targetValue = cloneDeep(toRaw(fieldValues.value));
      const sourceValue = cloneDeep(
        toRaw(issueStore.issueAssemble.value.issueVO.data)
      );
      console.log('saveChangedImmediately', sourceValue.dateRange, targetValue.dateRange);

      // if (targetValue.autoCalculateFlag === 0) {
      //   targetValue.estimatePoint = targetValue.estimatePoint;
      // }

      if (
        sourceValue.autoCalculateFlag === 1 &&
        sourceValue.estimatePoint !== ''
      ) {
        sourceValue.estimatePoint = targetValue.estimatePoint;
      }

      const diff = await omitNoValidateFields(
        deepDiffObj(sourceValue, targetValue),
        rootForm
      );

      if (
        fieldCodeMap.value.estimatePoint?.requiredFlag &&
        targetValue.estimatePoint === '' &&
        Reflect.has(diff, 'autoCalculateFlag')
      ) {
        delete diff.autoCalculateFlag;
      }

      if (diff.relationship) {
        delete diff.relationship;
      }
      if (diff.dateRange) {
        delete diff.dateRange;
      }
      if (diff.attachment) {
        delete diff.attachment;
      }

      if (diff.subtask) {
        delete diff.subtask;
      }

      if (diff.docs) {
        delete diff.docs;
      }

      if (Reflect.has(diff, 'figma')) {
        const transformFieldValue = (value: string | { fieldValue: string }) =>
          typeof value === 'string' ? value : value?.fieldValue;
        if (
          transformFieldValue(diff.figma) ===
          transformFieldValue(sourceValue.figma)
        ) {
          Reflect.deleteProperty(diff, 'figma');
        }
      }

      // deepDiffObj 会把 '' 和 null 当作相同的值，所以无法区分清空点数和自动计算，这里要做特殊处理
      if (
        Reflect.has(diff, 'autoCalculateFlag') &&
        !Reflect.has(diff, 'estimatePoint')
      ) {
        diff.estimatePoint = targetValue.estimatePoint;
      }

      if (Reflect.has(diff, 'estimatePoint')) {
        diff.autoCalculateFlag = targetValue.autoCalculateFlag;
      } else {
        Reflect.deleteProperty(diff, 'autoCalculateFlag');
      }

      layoutStore.allFields.value.forEach((field) => {
        if (
          field.controlCode === 'formula' &&
          Reflect.has(diff, field.fieldCode)
        ) {
          Reflect.deleteProperty(diff, field.fieldCode);
        }
        Reflect.deleteProperty(diff, field.fieldCode + 'Detail');
      });

      if (
        fieldValues.value.remainingLabourModified === 1 &&
        Reflect.has(diff, 'remainingLabour')
      ) {
        diff.remainingLabourModified = 1;
      }

      if (
        isArray(diff.labourRecord) &&
        diff.labourRecord.length > 0 &&
        diff.remainingLabourModified === 1 &&
        !Reflect.has(diff, 'remainingLabour')
      ) {
        diff.remainingLabour = fieldValues.value.remainingLabour;
      }

      // 当 diff 包含 status 时，校验所有必填字段是否已填写
      if (Reflect.has(diff, 'status')) {
        const requiredFields = layoutStore.allFields.value.filter(
          (f) => f.requiredFlag === 1 && f.fieldCode !== 'status'
        );
        const emptyRequiredFields = requiredFields.filter((f) => {
          const value = fieldValues.value[f.fieldCode];
          return isNil(value) || value === '' || (isArray(value) && value.length === 0);
        });
        if (emptyRequiredFields.length > 0) {
          // 触发必填字段的校验提示
          emptyRequiredFields.forEach((f) => {
            const formItem = rootForm.fields.find((item: any) => item.prop === f.fieldCode);
            formItem?.validate('');
          });
          // 回滚 status 的页面值
          set(fieldValues.value, 'status', sourceValue.status);
          window.console.warn('[saveChangedImmediately] status 变更被阻止，以下必填字段为空:', emptyRequiredFields.map((f) => f.fieldCode));
          return;
        }
      }

      if (Object.keys(diff).length > 0) {
        const oldValue = cloneDeep(issueStore.issueAssemble.value.issueVO.data);
        
        if (Reflect.has(diff, 'estimateLabour') && !Reflect.has(diff, 'totalEstimateLabour')) {
          diff.totalEstimateLabour = fieldValues.value.totalEstimateLabour;
        }
        let isSuccess = true;

        try {
          const records = await updateIssueImmediately(
            issueStore.issueAssemble.value.issueVO.id,
            diff
          );
          actionStore.markChanged();
          issueStore.issueAssemble.value.issueVO.data = cloneDeep(
            fieldValues.value
          );
          
          valueSavedEvent.trigger(diff);
          console.log(999999, diff);
          //#region 埋点
          const trackFields = Object.keys(diff)?.filter(
            (fieldCode) =>
              !['subtask', 'attachment', 'relationship'].includes(fieldCode)
          );
          const trackParams: Record<string, string> = {
            position: '详情',
            field: trackFields.join()
          };
          if (Object.keys(diff).includes('assignee')) {
            const assignedEffort: string =
              (
                document.querySelector(
                  '.el-form-item[data-field-code=assignee]'
                ) as any
              )?.__vue__?.$parent?.$refs?.field?.assignedEffort || 'none';
            const assignedResultMap: Record<string, string> = {
              none: '空',
              search: '搜索',
              recommend: '推荐'
            };
            trackParams.assignedResult =
              assignedResultMap[assignedEffort] || assignedEffort;
          }
          instance.$track.send('Issue_Update', trackParams, true);
          trackParams?.field === 'status' && instance.$track.send('Issue_StateChange', { form:'Web' }, true);
          if (diff.status) {
            const status = (optionsMap.value.status || []).find(
              (item) => item.value === diff.status
            );
            if (status?.extendValue === 4) {
              instance.$track.send('Issue_Done', {}, true);
            }
          }
          if (Object.keys(diff).includes('estimatePoint')) {
            instance.eventBus.$emit('issue-point-change');
          }
          //#endregion
        } catch (e) {
          isSuccess = false;
          window.console.error('[saveChangedImmediately] 更新失败，准备回滚字段:', Object.keys(diff), e);
        }
        if (!isSuccess && issueStore.issueAssemble.value) {
          issueStore.issueAssemble.value.issueVO.data = oldValue; // 回滚
          // 精确还原被修改字段的页面显示值
          Object.keys(diff).forEach((key) => {
            window.console.warn(`[saveChangedImmediately] 回滚字段 "${key}":`, diff[key], '->', oldValue[key]);
            set(fieldValues.value, key, cloneDeep(oldValue[key]));
          });
        }
      }
    }
  };

  const flushChanges = debounce(async () => {
    saveChangedImmediately();
  }, 200);

  let isCreating = false;
  const submitCreateIssue = async (isContinueCreate = false) => {
    if (shareStore.isVisitor.value) {
      return;
    }
    const rootForm = (document.getElementById('issue-root-form') as any)
      ?.__vue__;
    if (
      actionStore.action.value?.type === 'Create' &&
      !isCreating &&
      rootForm
    ) {
      isCreating = true;
      try {
        await validateForm(rootForm);
        const data: any = {
          ...fieldValues.value,
          projectId: projectStore.currentProjectId.value,
          issueTypeId: issueTypeStore.currentTypeId.value,
          attachment: fieldValues.value.attachment?.map((item: any) => item.id),
          activities: null,
          subtask: fieldValues.value.subtask?.filter((item: any) => !item.id),
          relationship: fieldValues.value.relationship?.map((item: any) => ({
            id: item.issueId,
            type: item.type
          })),
          workflowId: issueTypeStore.currentIssueType.value?.workflowId,
          sprint: isUndefined(fieldValues.value.sprint)
            ? actionStore.action.value.sprintId
            : fieldValues.value.sprint,
          sourceIssueId: actionStore.action.value.copyFromIssueId,
          docs: fieldValues.value.docs?.map((item: any) => item.docId)
        };

        if (Reflect.has(data, 'figma') && typeof data.figma === 'string') {
          data.figma = { fieldValue: data.figma };
        }

        const res = await createIssue(data);

        const eventData = transformVO(
          data,
          optionsMap.value,
          vuex.state.user.account.userId,
          res.subtask
        );
        instance.$track.send(
          'Issue_Create',
          { position: '顶部+号', fastCreate: false },
          true
        );

        instance.eventBus.$emit('issue:submit', res.id, true, eventData);
        instance.eventBus.$emit('issue:add-notice', {
          ...eventData,
          id: res.id,
          issueTypeIdVO: issueTypeStore.currentIssueType.value
        });
        if (isContinueCreate) {
          actionStore.createIssue(
            projectStore.currentProjectId.value,
            fieldValues.value.sprint ?? undefined,
            issueTypeStore.currentTypeId.value,
            undefined,
            undefined
          );
        } else {
          actionStore.close(res?.id);
        }
        return res;
      } catch (error) {
        console.error(error);
      } finally {
        isCreating = false;
        (document.activeElement as any)?.blur();
      }
    }
  };

  watch(fields, (fields) => {
    if (
      fields.length > 0 &&
      actionStore.actionType.value === 'Edit' &&
      issueStore.issueAssemble.value &&
      fields.every((field) => field.fieldCode !== 'subtask')
    ) {
      issueStore.issueAssemble.value.issueVO.data.subtask = null;
      fieldValues.value.subtask = [];
    }
  });

  return {
    members,
    fieldValues,
    setFieldValue,
    fieldCodeMap,
    optionsMap,
    flushChanges,
    updateIssueImmediately,
    fields,
    submitCreateIssue,
    onValueChanged: valueChangedEvent.on,
    triggerValueChanged: valueChangedEvent.trigger,
    refreshOptions,
    refreshMembers,
    onValueSaved: valueSavedEvent.on,
    saveChangedImmediately
  };
};

export type FieldsStore = ReturnType<typeof useFields>;
